/**
 * Email Address Processing Utilities
 *
 * This module contains utility functions for processing and validating email addresses
 * extracted from email headers and recipient arrays.
 */

import { EmailRecipientType } from '../../../api/cmail/cmail.dto';

export interface EmailRecipient {
  email: string;
  type?: EmailRecipientType;
}

export interface RecipientsByType {
  to: string[];
  cc: string[];
  bcc: string[];
}

/**
 * Extracts and parses email addresses from header fields
 * @param headerField - The header field string (e.g., "John Doe <john@example.com>, jane@example.com")
 * @returns Array of email addresses in lowercase
 */
export const extractEmailAddresses = (
  headerField: string | undefined
): string[] => {
  if (!headerField) {
    return [];
  }

  // Split by comma and extract email addresses
  return headerField
    .split(',')
    .map((addr) => {
      // Extract email from "Name <email@domain.com>" format or just "email@domain.com"
      const match =
        addr.match(/<([^>]+)>/) ||
        addr.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
      return match ? match[1].trim() : addr.trim();
    })
    .filter((email) => email.includes('@'))
    .map((email) => email.toLowerCase());
};

/**
 * Extracts the bare address from a single mailbox that may carry a display name.
 * @param emailOrFromField - "John Doe <john@example.com>" or "john@example.com"
 * @returns The address alone, or the trimmed input when there is no angle-bracket pair
 */
export const extractEmailAddress = (emailOrFromField: string): string => {
  const trimmedValue = (emailOrFromField || '').trim();
  const match = trimmedValue.match(/<([^>]+)>/);
  return (match && match[1] ? match[1] : trimmedValue).trim();
};

/**
 * How a sender reads in a message header: the display name, then the bare address.
 *
 * senderEmail is meant to hold a bare address, but sent messages stored before the send path was
 * fixed hold the whole "Name <address>" mailbox, which rendered as "Name <Name <address>>". The
 * address is unwrapped here so those rows read correctly, and the name is dropped when it is just
 * the address again -- which is what inbound mail stores when the From header carries no name.
 *
 * @param senderName - The display name, if the message has one
 * @param senderEmail - The stored sender address, bare or as a whole mailbox
 * @returns "Name <address>", or the address alone when there is no distinct name
 */
/**
 * A sender's display name and bare address, for showing them apart. `name`
 * is absent when there is none, or when it is just the address again.
 */
export const senderParts = (
  senderName: string | undefined,
  senderEmail: string
): { name?: string; address: string } => {
  const address = extractEmailAddress(senderEmail);
  const name = senderName?.trim();
  return name && name !== address ? { name, address } : { address };
};

export const formatSenderDisplay = (
  senderName: string | undefined,
  senderEmail: string
): string => {
  const { name, address } = senderParts(senderName, senderEmail);
  return name ? `${name} <${address}>` : address;
};

/**
 * Extracts recipients from receivers array format and categorizes them by type
 * @param receivers - Array of receiver objects with email and type
 * @returns Object with arrays of email addresses categorized by type (to, cc, bcc)
 */
export const extractRecipientsFromReceivers = (
  receivers: EmailRecipient[] = []
): RecipientsByType => {
  const to: string[] = [];
  const cc: string[] = [];
  const bcc: string[] = [];

  receivers.forEach((receiver) => {
    if (receiver.email && receiver.email.trim()) {
      const email = receiver.email.trim().toLowerCase();
      switch (receiver.type?.toUpperCase()) {
        case EmailRecipientType.TO:
          to.push(email);
          break;
        case EmailRecipientType.CC:
          cc.push(email);
          break;
        case EmailRecipientType.BCC:
          bcc.push(email);
          break;
        default:
          // If type is unknown, treat as TO
          to.push(email);
          break;
      }
    }
  });

  return { to, cc, bcc };
};

/**
 * Filters out duplicate email addresses from an array
 * @param emails - Array of email addresses
 * @returns Array with unique email addresses
 */
export const removeDuplicateEmails = (emails: string[]): string[] => {
  return emails.filter((email, index, arr) => arr.indexOf(email) === index);
};

/**
 * Filters out the current user's email from a list of recipients
 * @param emails - Array of email addresses
 * @param currentUserEmail - Current user's email address
 * @returns Array with current user's email removed
 */
export const filterOutCurrentUser = (
  emails: string[],
  currentUserEmail: string
): string[] => {
  const userEmailLower = currentUserEmail.toLowerCase();
  return emails.filter((email) => email !== userEmailLower);
};

/**
 * Combines multiple recipient arrays and removes duplicates
 * @param recipientArrays - Arrays of email addresses to combine
 * @returns Combined array with unique email addresses
 */
export const combineRecipients = (...recipientArrays: string[][]): string[] => {
  const allEmails = recipientArrays.flat();
  return removeDuplicateEmails(allEmails);
};

/**
 * Formats a list of email addresses for display
 * @param emails - Array of email addresses
 * @param separator - Separator to use between emails (default: ', ')
 * @returns Formatted string of email addresses
 */
export const formatEmailList = (emails: string[], separator = ', '): string => {
  return emails.join(separator);
};

/**
 * Validates if a string is a valid email address
 * @param email - Email address to validate
 * @returns True if valid email format, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

/**
 * Extracts email addresses from a comma-separated string and validates them
 * @param emailString - Comma-separated string of email addresses
 * @returns Object with valid and invalid email addresses
 */
export const validateEmailList = (
  emailString: string
): {
  valid: string[];
  invalid: string[];
} => {
  const emails = emailString
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

  const valid: string[] = [];
  const invalid: string[] = [];

  emails.forEach((email) => {
    if (isValidEmail(email)) {
      valid.push(email.toLowerCase());
    } else {
      invalid.push(email);
    }
  });

  return { valid, invalid };
};
